import { useNode } from "@craftjs/core";
import React, { useContext } from "react";
import { isUpdatedFromDefault } from "../../utils";

import {
  ColorInput,
  UnitInput,
  Tabs,
  PaddingInput,
  MarginInput,
  Spacer,
  AlignInput,
  ToggleInput,
  Panel,
  BorderInput,
  BorderRadiusInput,
  BoxShadowInput,
  TextInput,
  MultipleSelect,
  SelectInput,
  RadioButtonInput,
} from "../../input";

import { supportLibrary } from "../../block-supports";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { INPUT_LIMITS } from "../../input-limits";

export const FormSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const { blockState, setBlockState } = useContext(GrigoraDashboardContext);

  const {
    method,
    action,
    name,
    hideOnSuccess = true,
    hideOnFail = true,
    subscribeToNewsletter = true,
    width,
    widthTablet,
    widthMobile,
    height,
    heightTablet,
    heightMobile,
    maxHeight,
    maxHeightTablet,
    maxHeightMobile,
    maxWidth,
    maxWidthTablet,
    maxWidthMobile,
    minHeight,
    minHeightMobile,
    minHeightTablet,
    minWidth,
    minWidthTablet,
    minWidthMobile,
    marginTablet,
    marginMobile,
    margin,
    paddingMobile,
    paddingTablet,
    padding,
    textColor,
    linkColor,
    border,
    borderTablet,
    borderMobile,
    boxShadow,
    borderRadius,
    borderRadiusTablet,
    borderRadiusMobile,
    gap = "1rem",
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <RadioButtonInput
            label="View"
            isResetVisible={false}
            className={"w-full"}
            value={blockState(id)?.state ?? "default"}
            onChange={(e) => {
              setBlockState(id, "state", e);
            }}
            options={[
              {
                value: "default",
                label: "Default",
              },
              {
                value: "success",
                label: "Submit",
              },
              {
                value: "fail",
                label: "Fail",
              },
            ]}
          />
          {blockState(id)?.state === "success" && (
            <>
              <ToggleInput
                isResetVisible={false}
                value={hideOnSuccess}
                onChange={(val) => {
                  setProp((props) => {
                    props.hideOnSuccess = val;
                  });
                }}
                label={"Hide Form On Success"}
              />
            </>
          )}
          {blockState(id)?.state === "fail" && (
            <>
              <ToggleInput
                isResetVisible={false}
                value={hideOnFail}
                onChange={(val) => {
                  setProp((props) => {
                    props.hideOnFail = val;
                  });
                }}
                label={"Hide Form On Fail"}
              />
            </>
          )}
          <SelectInput
            isResetVisible={false}
            value={method}
            onChange={(val) => {
              setProp((props) => {
                props.method = val;
              });
            }}
            options={[
              { label: "GET", value: "get" },
              { label: "POST", value: "post" },
            ]}
            label="Method"
          />
          <TextInput
            minLength={INPUT_LIMITS["grigora-form-action"].minlength}
            maxLength={INPUT_LIMITS["grigora-form-action"].maxlength}
            isResetVisible={false}
            value={action}
            onChange={(val) => {
              setProp((props) => {
                props.action = val;
              });
            }}
            label="Action"
            placeholder="Grigora Form Submissions"
          />
          <ToggleInput
            isResetVisible={false}
            value={subscribeToNewsletter}
            onChange={(val) => {
              setProp((props) => {
                props.subscribeToNewsletter = val;
              });
            }}
            label={"Subscribe to Newsletter"}
          />
          {subscribeToNewsletter && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-[12px] p-3 mt-2 mb-2">
              <div className="text-sm text-yellow-800">
                ⚠️ Make sure your email input has name &quot;email&quot;.
              </div>
            </div>
          )}
          <TextInput
            minLength={INPUT_LIMITS["simple-contact-form-name"].minlength}
            maxLength={INPUT_LIMITS["simple-contact-form-name"].maxlength}
            isResetVisible={false}
            value={name}
            onChange={(val) => {
              setProp((props) => {
                props.name = val;
              });
            }}
            label="Name"
            placeholder="Simple Contact Form"
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: width },
            { key: widthMobile },
            { key: widthTablet },
            { key: height },
            { key: heightTablet },
            { key: heightMobile },
            { key: maxHeight },
            { key: maxHeightTablet },
            { key: maxHeightMobile },
            { key: maxWidth },
            { key: maxWidthTablet },
            { key: maxWidthMobile },
            { key: minHeightMobile },
            { key: minHeightTablet },
            { key: minHeight },
            { key: minWidthMobile },
            { key: minWidthTablet },
            { key: minWidth },
            { key: gap },
            { key: maxHeight },
          ])}
          id={`block-setting-${id}-dimensions`}
          title={"Dimensions"}
          initialOpen={false}
        >
          <UnitInput
            auto
            value={width}
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
            resetValue={"100%"}
            resetValueTablet={"100%"}
            resetValueMobile={"100%"}
          />
          <UnitInput
            resetValue={undefined}
            auto
            value={height}
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={maxHeight}
            isResponsive={true}
            valueTablet={maxHeightTablet}
            valueMobile={maxHeightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.maxHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.maxHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.maxHeightMobile = val;
              });
            }}
            label={"Max Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={maxWidth}
            isResponsive={true}
            valueTablet={maxWidthTablet}
            valueMobile={maxWidthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.maxWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.maxWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.maxWidthMobile = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            valueMobile={minHeightMobile}
            valueTablet={minHeightTablet}
            value={minHeight}
            onChange={(val) => {
              setProp((props) => {
                props.minHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.minHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.minHeightMobile = val;
              });
            }}
            label={"Min Height"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            valueMobile={minWidthMobile}
            valueTablet={minWidthTablet}
            value={minWidth}
            onChange={(val) => {
              setProp((props) => {
                props.minWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.minWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.minWidthMobile = val;
              });
            }}
            label={"Min Width"}
          />
          <UnitInput
            resetValue={"1rem"}
            value={gap}
            onChange={(val) => {
              setProp((props) => {
                props.gap = val;
              });
            }}
            label={"Gap"}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: textColor },
              { key: linkColor },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <ColorInput
              resetValue={undefined}
              value={textColor}
              onChange={(val) => {
                setProp((props) => {
                  props.textColor = val;
                });
              }}
              label={"Text"}
            />
            <ColorInput
              resetValue={undefined}
              value={linkColor}
              onChange={(val) => {
                setProp((props) => {
                  props.linkColor = val;
                });
              }}
              label={"Link"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: marginTablet },
            { key: marginMobile },
            { key: margin },
            { key: paddingTablet },
            { key: paddingMobile },
            {
              key: padding,
              default: {
                top: "20px",
                bottom: "20px",
                left: "20px",
                right: "20px",
              },
            },
          ])}
          id={`block-setting-${id}-form`}
          initialOpen={true}
          title={"Layout"}
        >
          <MarginInput
            isResponsive={true}
            valueTablet={marginTablet}
            valueMobile={marginMobile}
            resetValue={undefined}
            value={margin}
            onChange={(val) => {
              setProp((props) => {
                props.margin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.marginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.marginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            valueTablet={paddingTablet}
            valueMobile={paddingMobile}
            resetValue={{
              top: "20px",
              bottom: "20px",
              left: "20px",
              right: "20px",
            }}
            value={padding}
            onChange={(val) => {
              setProp((props) => {
                props.padding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.paddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.paddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderTablet },
            { key: borderMobile },
            { key: borderRadius },
          ])}
          id={`block-setting-${id}-layout`}
          initialOpen={true}
          title={"Border"}
        >
          <BorderInput
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
            valueTablet={borderTablet}
            setValueTablet={(val) => {
              setProp((props) => {
                props.borderTablet = val;
              });
            }}
            valueMobile={borderMobile}
            setValueMobile={(val) => {
              setProp((props) => {
                props.borderMobile = val;
              });
            }}
          />
          <BorderRadiusInput
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: boxShadow }])}
          id={`block-setting-${id}-box-shadow`}
          title={"Box Shadow"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: blockState(id)?.state },
        { key: method },
        { key: name },
        { key: subscribeToNewsletter },
        { key: width },
        { key: widthMobile },
        { key: widthTablet },
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
        { key: maxHeight },
        { key: maxHeightTablet },
        { key: maxHeightMobile },
        { key: maxWidth },
        { key: maxWidthTablet },
        { key: maxWidthMobile },
        { key: minHeightMobile },
        { key: minHeightTablet },
        { key: minHeight },
        { key: minWidthMobile },
        { key: minWidthTablet },
        { key: minWidth },
        { key: gap },
        { key: maxHeight },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([{ key: textColor }, { key: linkColor }]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: marginTablet },
        { key: marginMobile },
        { key: margin },
        { key: paddingTablet },
        { key: paddingMobile },
        {
          key: padding,
          default: {
            top: "20px",
            bottom: "20px",
            left: "20px",
            right: "20px",
          },
        },
        { key: border },
        { key: borderTablet },
        { key: borderMobile },
        { key: borderRadius },
        { key: boxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
